import { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Button,
  Divider,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { toast } from "sonner";
import { uploadDocument, viewDocument } from "./utiles/DocumentService";

const NAVY = "#123B5D";
const BORDER = "#DCE5EE";
const SURFACE = "#F5F8FC";
const MUTED = "#64748B";
const RED = "#D32F2F";

const Sidebar = ({
  isOpen,
  onClose,
  workDocs,
  onFilesUpdate,
  uploadedFilePaths,
  onTempFilesDelete,
  onPermentFilesDelete,
  permitNo,
  loading,
  setLoading,
}) => {
  const apiUrl = localStorage.getItem("apiUrl");
  const dbTokenData = localStorage.getItem("dbTokenData");

  const headers = {
    Authorization: `Bearer ${dbTokenData}`,
    "Content-Type": "application/json",
    dbMaster: "",
  };

  const envCode = localStorage.getItem("envcode") || "Prod";
  const orgcode = localStorage.getItem("custCode") || "ptw";

  const [attachedFiles, setAttachedFiles] = useState([]);
  const [docUrls, setDocUrls] = useState([]);
  const [error, setError] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [selectedDocUrl, setSelectedDocUrl] = useState(null);

  useEffect(() => {
    const allDocs = [...workDocs, ...uploadedFilePaths];

    if (allDocs.length > 0) {
      try {
        const urls = createDataUrls(allDocs);

        setDocUrls(urls);
        setLoading(false);
      } catch (err) {
        setError("Failed to generate document URLs");
        setLoading(false);
        console.error("Error generating document URLs:", err);
      }
    } else {
      setDocUrls([]);
      setLoading(false);
    }
  }, [workDocs, uploadedFilePaths]);

  const createDataUrls = (docs) => {
    return docs.map((docItem) => ({
      id: docItem.id,
      name: docItem.name,
      url: docItem.doc || docItem.url,
      type: docItem.type,
    }));
  };

  const getWorkdocs = (newDocs) => {
    setDocUrls((prevDocs) => [...prevDocs, ...createDataUrls(newDocs)]);
  };

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    const validFiles = selectedFiles.filter(
      (file) => file.size < 5 * 1024 * 1024,
    );

    if (validFiles.length < selectedFiles.length) {
      toast.warning("Some files exceed the size limit and were not added.");
    }

    setAttachedFiles((prev) => [...prev, ...validFiles]);

    e.target.value = "";
  };

  const fetchEmployeeImage = async (logoNames) => {
    setLoading(true);

    try {
      const folderPath = `${orgcode}/ptw_${permitNo}`;

      const response = await viewDocument({
        projectCode: "Ptw",
        envCode,
        folderPath,
        fileName: logoNames,
      });

      if (response.cd === 1) {
        return [];
      }

      if (!response) {
        console.warn("No file data found");
        return [];
      }

      const mimeType = logoNames.toLowerCase().endsWith(".pdf")
        ? "application/pdf"
        : "image/*";

      const binaryString = atob(response);

      const bytes = new Uint8Array(binaryString.length);

      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      const blob = new Blob([bytes], {
        type: mimeType,
      });

      const url = URL.createObjectURL(blob);

      return [
        {
          name: logoNames,
          url,
        },
      ];
    } catch (err) {
      console.error("Failed to fetch image:", err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async () => {
    if (attachedFiles.length === 0) {
      toast.warning("No files to upload.");
      return;
    }

    setLoading(true);

    const uploadedDocs = [];
    const newUploadedFilePaths = [];

    for (let i = 0; i < attachedFiles.length; i++) {
      const file = attachedFiles[i];

      try {
        const folderPath = `${orgcode}/ptw_${permitNo}`;

        const uploadRes = await uploadDocument({
          file,
          projectCode: "Ptw",
          envCode,
          folderPath,
        });

        if (uploadRes?.cd === 1) {
          toast.error(uploadRes.description);
          setLoading(false);
          return;
        }

        uploadedDocs.push({
          doc: uploadRes,
          name: file.name,
          type: file.type,
        });

        newUploadedFilePaths.push({
          id: Math.random().toString(36).substr(2, 9),
          doc: uploadRes,
          name: file.name,
          type: file.type,
        });

        toast.success(
          `File ${file.name} uploaded successfully! Preview is available. Please review the file before updating.`,
        );
      } catch (uploadErr) {
        console.error(`Error uploading ${file.name}:`, uploadErr);
        toast.error(`Upload failed for ${file.name}`);
      }
    }

    if (uploadedDocs.length > 0) {
      const docNames = uploadedDocs.map((d) => d.doc).join(",");

      const fileNames = uploadedDocs.map((d) => d.name).join(",");

      const fileTypes = uploadedDocs.map((d) => d.type).join(",");

      const workDocSave = {
        workid: permitNo,
        doc: docNames,
        name: fileNames,
        type: fileTypes,
      };

      console.log("Prepared CSV structure for backend:", workDocSave);
    }

    if (newUploadedFilePaths.length > 0) {
      getWorkdocs(newUploadedFilePaths);
      onFilesUpdate(newUploadedFilePaths);
    }

    setAttachedFiles([]);
    setLoading(false);
  };

  const openModal = async (fileNameCSV) => {
    const previews = await fetchEmployeeImage(fileNameCSV);

    if (previews.length > 0) {
      setSelectedDocUrl(previews);
    } else {
      toast.warning("No preview available for selected document.");
    }
  };

  const closeModal = () => {
    if (Array.isArray(selectedDocUrl)) {
      selectedDocUrl.forEach((doc) => {
        if (doc.url?.startsWith("blob:")) {
          URL.revokeObjectURL(doc.url);
        }
      });
    }

    setSelectedDocUrl(null);
  };

  const handleDelete = async (id) => {
    if (typeof id === "number") {
      setLoading(true);

      try {
        const response = await axios.delete(`${apiUrl}workdoc/${id}`, {
          headers,
        });

        if (response.data.cd === 0) {
          const updatedDocs = docUrls.filter((doc) => doc.id !== id);

          setDocUrls(updatedDocs);

          const updatedUploads = workDocs.filter((doc) => doc.id !== id);

          onPermentFilesDelete(updatedUploads);

          toast.success("Document deleted successfully.");
        } else {
          toast.error(
            "Failed to delete document: " + response.data.description,
          );
        }
      } catch (error) {
        console.error("Delete error:", error);
        toast.error("An error occurred while deleting the document.");
      } finally {
        setLoading(false);
      }
    } else {
      const updatedDocs = docUrls.filter((doc) => doc.id !== id);

      setDocUrls(updatedDocs);

      const updatedUploads = uploadedFilePaths.filter((doc) => doc.id !== id);

      onTempFilesDelete(updatedUploads);

      toast.success("Document deleted successfully.");
    }

    setConfirmDeleteId(null);
  };

  const getFileIcon = (name) => {
    const lower = name?.toLowerCase() || "";

    if (lower.endsWith(".pdf")) {
      return (
        <PictureAsPdfOutlinedIcon
          sx={{
            fontSize: 22,
            color: RED,
          }}
        />
      );
    }

    return (
      <ImageOutlinedIcon
        sx={{
          fontSize: 22,
          color: "#3B82F6",
        }}
      />
    );
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={isOpen}
        onClose={onClose}
        ModalProps={{
          keepMounted: true,
          sx: {
            zIndex: 1500,
          },
        }}
        sx={{
          zIndex: 1500,
          "& .MuiDrawer-paper": {
            width: {
              xs: "100%",
              sm: 460,
              md: 480,
            },
            maxWidth: "100%",
            boxSizing: "border-box",
            backgroundColor: SURFACE,
            boxShadow: "-10px 0 30px rgba(15,45,75,0.20)",
            borderLeft: `1px solid ${BORDER}`,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            minHeight: 64,
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: NAVY,
            color: "#FFFFFF",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: 18,
                fontWeight: 700,
                lineHeight: 1.2,
              }}
            >
              Documents
            </Typography>
            <Typography
              sx={{
                mt: 0.25,
                fontSize: 11.5,
                color: "rgba(255,255,255,0.75)",
              }}
            >
              Permit documents
            </Typography>
          </Box>

          <IconButton
            onClick={onClose}
            sx={{
              color: "#FFFFFF",
              width: 36,
              height: 36,
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.10)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 1.5,
            py: 1.5,
          }}
        >
          {error && (
            <Paper
              elevation={0}
              sx={{
                px: 1.5,
                py: 1.25,
                mb: 1.5,
                borderRadius: "8px",
                border: "1px solid #F3C5C5",
                backgroundColor: "#FFF5F5",
                color: RED,
                fontSize: 12.5,
              }}
            >
              {error}
            </Paper>
          )}

          {docUrls.length > 0 ? (
            <Box
              sx={{
                display: "grid",
                gap: 1,
              }}
            >
              {docUrls.map(({ id, name, url, type }) => {
                const fileName = url?.split("/").pop();

                return (
                  <Paper
                    key={id}
                    elevation={0}
                    sx={{
                      p: 1.25,
                      border: `1px solid ${BORDER}`,
                      borderRadius: "9px",
                      backgroundColor: "#FFFFFF",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        sx={{
                          width: 38,
                          height: 38,
                          flexShrink: 0,
                          borderRadius: "8px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#EFF5FA",
                        }}
                      >
                        {getFileIcon(name || fileName)}
                      </Box>

                      <Box
                        sx={{
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: 13,
                            fontWeight: 600,
                            color: "#243746",
                            wordBreak: "break-word",
                            lineHeight: 1.35,
                          }}
                        >
                          {name || fileName}
                        </Typography>

                        {type && (
                          <Typography
                            sx={{
                              mt: 0.2,
                              fontSize: 10.5,
                              color: MUTED,
                              textTransform: "uppercase",
                            }}
                          >
                            {type.split("/").pop()}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<VisibilityOutlinedIcon />}
                        onClick={() => openModal(url)}
                        sx={{
                          flex: 1,
                          minHeight: 34,
                          borderRadius: "7px",
                          textTransform: "none",
                          fontSize: 12,
                          fontWeight: 600,
                          color: NAVY,
                          borderColor: "#B9CBD9",
                          "&:hover": {
                            borderColor: NAVY,
                            backgroundColor: "#F3F7FB",
                          },
                        }}
                      >
                        Preview
                      </Button>

                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<DeleteOutlineOutlinedIcon />}
                        onClick={() => setConfirmDeleteId(id)}
                        sx={{
                          flex: 1,
                          minHeight: 34,
                          borderRadius: "7px",
                          textTransform: "none",
                          fontSize: 12,
                          fontWeight: 600,
                          color: RED,
                          borderColor: "#E6B7B7",
                          "&:hover": {
                            borderColor: RED,
                            backgroundColor: "#FFF5F5",
                          },
                        }}
                      >
                        Delete
                      </Button>
                    </Box>
                  </Paper>
                );
              })}
            </Box>
          ) : (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: "center",
                border: `1px dashed #C7D5E1`,
                borderRadius: "10px",
                backgroundColor: "#FFFFFF",
              }}
            >
              <InsertDriveFileOutlinedIcon
                sx={{
                  fontSize: 34,
                  color: "#A7B7C5",
                  mb: 0.75,
                }}
              />
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: "#526273",
                }}
              >
                No documents available
              </Typography>
              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: 11.5,
                  color: "#94A3B8",
                }}
              >
                Upload documents below
              </Typography>
            </Paper>
          )}
        </Box>

        <Divider />

        <Box
          sx={{
            p: 1.5,
            backgroundColor: "#FFFFFF",
          }}
        >
          <Box
            sx={{
              border: `1px solid ${BORDER}`,
              borderRadius: "9px",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1,
                backgroundColor: "#EEF4F9",
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                borderBottom: `1px solid ${BORDER}`,
              }}
            >
              <UploadFileOutlinedIcon
                sx={{
                  fontSize: 18,
                  color: NAVY,
                }}
              />
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: NAVY,
                }}
              >
                Upload Documents
              </Typography>
            </Box>

            <Box
              sx={{
                p: 1.5,
              }}
            >
              <Box
                component="label"
                sx={{
                  minHeight: 80,
                  border: "1.5px dashed #B9CBD9",
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  backgroundColor: "#FAFCFE",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "#7F9BB2",
                    backgroundColor: "#F5F9FC",
                  },
                }}
              >
                <CloudUploadOutlinedIcon
                  sx={{
                    fontSize: 28,
                    color: "#7892A7",
                    mb: 0.25,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: "#526273",
                  }}
                >
                  Choose files
                </Typography>
                <Typography
                  sx={{
                    fontSize: 10.5,
                    color: "#94A3B8",
                    mt: 0.25,
                  }}
                >
                  PDF, JPG or PNG · Max 5 MB
                </Typography>

                <input
                  type="file"
                  name="files"
                  multiple
                  accept=".pdf,.png,.jpg"
                  hidden
                  onChange={handleFileChange}
                />
              </Box>

              {attachedFiles.length > 0 && (
                <Box
                  sx={{
                    mt: 1,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                  }}
                >
                  {attachedFiles.map((file, index) => (
                    <Chip
                      key={index}
                      icon={<InsertDriveFileOutlinedIcon />}
                      label={file.name}
                      size="small"
                      sx={{
                        maxWidth: "100%",
                        backgroundColor: "#EEF4F9",
                        color: "#33485C",
                        fontSize: 11,
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        },
                      }}
                    />
                  ))}
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                startIcon={<CloudUploadOutlinedIcon />}
                onClick={handleUpload}
                disabled={attachedFiles.length === 0 || loading}
                sx={{
                  mt: 1.25,
                  minHeight: 38,
                  borderRadius: "7px",
                  textTransform: "none",
                  fontSize: 12.5,
                  fontWeight: 700,
                  backgroundColor: NAVY,
                  "&:hover": {
                    backgroundColor: "#0E304B",
                  },
                }}
              >
                Upload
              </Button>
            </Box>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={Array.isArray(selectedDocUrl) && selectedDocUrl.length > 0}
        onClose={closeModal}
        fullWidth
        maxWidth="md"
        sx={{
          zIndex: 1600,
        }}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            overflow: "hidden",
            border: `1px solid ${BORDER}`,
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2,
            py: 1.25,
            backgroundColor: NAVY,
            color: "#FFFFFF",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 15,
            fontWeight: 700,
          }}
        >
          Document Preview
          <IconButton
            onClick={closeModal}
            sx={{
              color: "#FFFFFF",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.10)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: 1.5,
            backgroundColor: SURFACE,
          }}
        >
          {selectedDocUrl?.map(({ name, url }) => (
            <Box
              key={name}
              sx={{
                width: "100%",
              }}
            >
              <Typography
                sx={{
                  mb: 1,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "#33485C",
                }}
              >
                {name}
              </Typography>

              <Box
                sx={{
                  width: "100%",
                  minHeight: 360,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "#FFFFFF",
                  border: `1px solid ${BORDER}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                {name.match(/\.(jpeg|jpg|png|gif)$/i) ? (
                  <Box
                    component="img"
                    src={url}
                    alt={name}
                    sx={{
                      display: "block",
                      maxWidth: "100%",
                      maxHeight: "65vh",
                      objectFit: "contain",
                    }}
                  />
                ) : name.toLowerCase().endsWith(".pdf") ? (
                  <Box
                    component="iframe"
                    src={url}
                    title={name}
                    sx={{
                      width: "100%",
                      height: 600,
                      border: "none",
                    }}
                  />
                ) : (
                  <Typography
                    sx={{
                      color: MUTED,
                      fontSize: 13,
                    }}
                  >
                    Unsupported file type
                  </Typography>
                )}
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.25,
            backgroundColor: "#FFFFFF",
            justifyContent: "center",
          }}
        >
          <Button
            variant="contained"
            onClick={closeModal}
            sx={{
              minWidth: 90,
              minHeight: 36,
              borderRadius: "7px",
              textTransform: "none",
              fontWeight: 700,
              backgroundColor: NAVY,
              "&:hover": {
                backgroundColor: "#0E304B",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
        sx={{
          zIndex: 1700,
        }}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            border: `1px solid ${BORDER}`,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2,
            pt: 2,
            pb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
            fontSize: 15,
            fontWeight: 700,
            color: NAVY,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: "9px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#FFF4DE",
            }}
          >
            <WarningAmberOutlinedIcon
              sx={{
                color: "#C57A00",
              }}
            />
          </Box>
          Delete Document
        </DialogTitle>

        <DialogContent
          sx={{
            px: 2,
            pb: 1,
          }}
        >
          <Typography
            sx={{
              fontSize: 13,
              color: "#526273",
              lineHeight: 1.5,
            }}
          >
            Are you sure you want to delete this document?
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2,
            py: 1.5,
            gap: 1,
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setConfirmDeleteId(null)}
            sx={{
              minWidth: 85,
              minHeight: 36,
              borderRadius: "7px",
              textTransform: "none",
              fontWeight: 600,
              color: NAVY,
              borderColor: "#B9CBD9",
            }}
          >
            Cancel
          </Button>

          <Button
            variant="contained"
            onClick={() => {
              handleDelete(confirmDeleteId);
              setConfirmDeleteId(null);
            }}
            sx={{
              minWidth: 100,
              minHeight: 36,
              borderRadius: "7px",
              textTransform: "none",
              fontWeight: 700,
              backgroundColor: RED,
              "&:hover": {
                backgroundColor: "#B71C1C",
              },
            }}
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Sidebar;
